import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { SecondMarketAPI } from "services/second-market";
import {
  getProductOffering,
  getProductOfferingSuccess,
  getProductOfferingFailure,
  getProductOfferingDetail,
  getProductOfferingDetailSuccess,
  getProductOfferingDetailFailure,
  initSecondMarketDetail,
  verifyReceiveAccountSuccess,
  verifyReceiveAccountFailure,
  getConfirmationInfo,
  sendOTP,
  reSendOTP,
  confirmPayment,
  verifyReceiveAccount,
  getConfirmationInfoSuccess,
  getConfirmationInfoFailure,
  confirmPaymentSuccess,
  confirmPaymentFailure,
  sendOTPSuccess,
  sendOTPFailure,
  reSendOTPSuccess,
  reSendOTPFailure,
  getCustomerOutstandingSuccess,
  getCustomerOutstandingFailure,
  getSecuritiesDetailFailure,
  getSecuritiesDetailSuccess,
  getCustomerOutstanding,
  getSecuritiesDetail,
  fetchingProductDetailDone,
  requestApproveLombard,
  requestApproveLombardSuccess,
  requestApproveLombardFailure,
  cancelLombard,
  cancelLombardSuccess,
  cancelLombardFailure,
} from "redux/reducers/second-market";
import { push, replace } from "connected-react-router";
import { ErrorType, getRoutePath, Routes } from "const";
import moment from "moment-timezone";
import { SubscriptionAPI } from "services/subscription";
import { SecondMarketSelector } from "redux/selector/second-market-selector";
import { MasterSelector } from "redux/selector";
import { CustomerSelector } from "redux/selector/customer-selector";
import { getAccountByAccountNo } from "utils/second-util";
import { filterFields, removeNullishFields, toUrlSearchParam } from "utils/common-util";
import { signOut } from "redux/reducers/auth";
import { openModal } from "redux/reducers/central-modal";
import { calculate, workAroundNumber } from "utils/math";
import concat from "lodash/concat";
import { DisclaimerType } from "const";
import { checkTitleAgainstGender } from "utils/personal-info-util";
import { getTitleSuccess } from "redux/reducers/master";
import { isTitleOther } from "utils/customer-util";
import { isSystemCloseError } from "const/const-helper";

const mappingBondTypeAliasToShort = (bondTypeAlias) => {
  return {
    [bondTypeAlias]: bondTypeAlias,
    GOVERNMENT: "GB",
    CORPORATE: "CORP",
  }[bondTypeAlias];
};

const getProductOfferingListSaga = function* (action) {
  try {
    let condition = action.payload;
    const response = yield call(SecondMarketAPI.getProductOfferingList, { condition });

    let productOfferingList =
      response.data?.productOfferingList?.map((sec) => {
        sec.bondTypeAlias = mappingBondTypeAliasToShort(sec.bondTypeAlias);
        return sec;
      }) ?? [];

    yield put(getProductOfferingSuccess({ productOfferingList }));
  } catch (error) {
    if (isSystemCloseError(error?.code)) {
      yield put(getProductOfferingSuccess({ productOfferingList: [] }));
    }

    yield put(getProductOfferingFailure(error));
  }
};

const getProductOfferingDetailSaga = function* ({ payload: product }) {
  try {
    const response = yield call(SecondMarketAPI.getProductOfferingDetail, {
      securityCode: product.securityCode,
    });

    yield put(fetchingProductDetailDone());

    let productOffering = response.data?.productOffering;
    let settlementDateList = productOffering.settlementDateList;

    yield call(initProductOfferingDetail, productOffering);
    yield put(getProductOfferingDetailSuccess({ productOffering, settlementDateList }));
    yield put(push(getRoutePath(Routes.Secondary.ProductDetail, product.securityCode)));
  } catch (error) {
    yield put(getProductOfferingDetailFailure(error));
  }
};

function* initProductOfferingDetail(product = {}) {
  const initSecondMarketDetailPayload = {
    unit: product.minimumUnit,
    unitBaht: calculate(`${workAroundNumber(product.minimumUnit)} * ${workAroundNumber(product.initPar)}`),
    brokerageAccNo: product.brokerageAccNo,
  };

  if (product.settlementDateList) {
    initSecondMarketDetailPayload.settlementDate = moment(product.settlementDateList[0].settlementDate);
  }

  //bondType==GB and  Symbol start with SB and nohave KTB custodian // **** comment receiveMethod listening from check inventory service
  // if (product.bondType === BondType.GB || product.bondType === BondType.Bond) {
  //   initSecondMarketDetailPayload.receiveMethod = ReceiveMethod.KTB //
  // } else {
  //   initSecondMarketDetailPayload.receiveMethod = ReceiveMethod.BROKER  //custodian
  //   initSecondMarketDetailPayload.receiveAccountNumber = ''
  //   initSecondMarketDetailPayload.receiveSecurityCompanyCode = "425"
  // }

  yield put(initSecondMarketDetail(initSecondMarketDetailPayload));
}

const verifyReceiveAccountSaga = function* ({ payload }) {
  try {
    if (typeof payload?.accountNo !== "string" || !payload.accountNo.trim()) {
      yield put(verifyReceiveAccountSuccess({ isAccount: false }));
      return;
    }

    const response = yield call(SubscriptionAPI.isAccountNumber, payload);
    const isAccount = response.data.result.account;
    yield put(verifyReceiveAccountSuccess({ isAccount }));
  } catch (error) {
    yield put(verifyReceiveAccountFailure(error));
  }
};

function* getConfirmationInfoSaga(action) {
  try {
    let info;

    if (action.payload?.confirmation) {
      info = action.payload.confirmation;
    } else {
      const response = yield call(SecondMarketAPI.getConfirmationInfo);
      info = response.data.confirmation;
    }

    // check gender and title matched
    const checkResult = checkTitleAgainstGender(info);
    info = checkResult.info;
    if (checkResult.prepareTitle) {
      yield put(getTitleSuccess({ options: checkResult.titles, thai: info.thai }));
    }

    yield put(
      getConfirmationInfoSuccess({
        ...info,
      }),
    );
    action.payload?.onComplete?.();
  } catch (error) {
    yield put(getConfirmationInfoFailure(error));
  }
}

const sendOTPSaga = function* ({ payload }) {
  try {
    const sendFirstOTP = yield select(SecondMarketSelector.sendFirstOTP);
    if (!sendFirstOTP) {
      const response = yield call(SecondMarketAPI.sendOTP, payload);
      const refOtp = response.data.refOtp;
      yield put(sendOTPSuccess(refOtp));
    }
  } catch (error) {
    yield put(sendOTPFailure(error));
  }
};

const reSendOTPSaga = function* ({ payload }) {
  try {
    const response = yield call(SecondMarketAPI.sendOTP, payload);
    const refOtp = response.data.refOtp;
    yield put(reSendOTPSuccess(refOtp));
  } catch (error) {
    yield put(reSendOTPFailure(error));
  }
};

const confirmPaymentSaga = function* ({ payload: payloadOTP }) {
  try {
    const secondMarketDetail = yield select(SecondMarketSelector.secondMarketDetail);
    const productOfferingDetail = yield select(SecondMarketSelector.productOfferingDetail);
    const locale = yield select(MasterSelector.locale);

    const isBuy = secondMarketDetail.bankDirection === "Buy";

    let payload;

    //* Lombard
    if (secondMarketDetail.useLombard) {
      //* Buy
      if (isBuy) {
        payload = {
          otpNo: payloadOTP.data.otp,
          otpRef: secondMarketDetail.refOtp,
          drawdownId: secondMarketDetail.drawdownId,
          title: secondMarketDetail.title,
          gender: secondMarketDetail.gender,
          language: locale,
        };
      }
      //* Sell
      else {
        payload = {
          otpNo: payloadOTP.data.otp,
          otpRef: secondMarketDetail.refOtp,
          securityCode: productOfferingDetail.securityCode,
          settlementDate: secondMarketDetail.settlementData.settlementDate,
          refPricingNo: secondMarketDetail.settlementData.refPricingNo,
          receiveAccountRef: secondMarketDetail.receiveAccountRef,
          repaymentFlag: secondMarketDetail.repaymentFlag,
          acceptDiscList: secondMarketDetail.acceptDiscList,
          unit: secondMarketDetail.unit,
          title: secondMarketDetail.title,
          titleOther: secondMarketDetail.titleOther,
          isGenderAndTitleInvalid: secondMarketDetail.isGenderAndTitleInvalid,
          gender: secondMarketDetail.gender,
          language: locale,
        };

        if (secondMarketDetail.acceptDisc04) {
          payload.acceptDiscList = concat(payload.acceptDiscList, DisclaimerType.AcceptWarningRisk);
          delete payload.acceptDisc04;
        }
      }
    }
    //* Normal
    else {
      payload = {
        ...productOfferingDetail,
        ...secondMarketDetail,
        otpNo: payloadOTP.data.otp,
        otpRef: secondMarketDetail.refOtp,
        unit: secondMarketDetail.unit,
        totalPayment: secondMarketDetail.baht,
        language: locale,
        securitiesCompCode: secondMarketDetail.receiveSecurityCompanyCode,
        securitiesCompAccNo: secondMarketDetail.receiveAccountNumber,
        dcmConfirm: "Y",
        settlementDate: secondMarketDetail.settlementData?.settlementDate,
        direction: secondMarketDetail.bankDirection,
        refPricingNo: secondMarketDetail.settlementData?.refPricingNo,
      };

      if (isBuy) {
        const accounts = yield select(CustomerSelector.globalAccounts);
        payload.paymentAccountRef = getAccountByAccountNo(payload.accountNo, accounts).accountId;
        delete payload.accountNo;
      }

      // payment account
      if (secondMarketDetail.acceptDisc04) {
        payload.acceptDiscList = concat(payload.acceptDiscList, DisclaimerType.AcceptWarningRisk);
        delete payload.acceptDisc04;
      }
    }

    // title
    if (payload.isGenderAndTitleInvalid && isTitleOther(payload.title)) {
      payload.title = payload.titleOther;
    }

    const referralInfo = secondMarketDetail.referralInfo;
    if (referralInfo && referralInfo.isVerified && referralInfo.referralCode && referralInfo.branchCode) {
      payload.recommenderCode = referralInfo.referralCode;
      payload.recommendBranchCode = referralInfo.branchCode;
    }

    // filter fields
    payload = removeNullishFields(filterFields(payload, confirmPaymentFields));
    const endpoint = secondMarketDetail.useLombard
      ? isBuy
        ? SecondMarketAPI.confirmPaymentLombard
        : SecondMarketAPI.confirmSellLombard
      : SecondMarketAPI.confirmPayment;
    const response = yield call(endpoint, payload);
    const paymentInfo = response.data.confirmation;
    yield put(confirmPaymentSuccess(paymentInfo));
    payloadOTP.onComplete(); // goto summary page
  } catch (error) {
    switch (error.code) {
      case ErrorType.OTP_FAIL_LIMIT_EXCEED:
        const errorConst = (yield select(MasterSelector.errorConst))?.[ErrorType.OTP_FAIL_LIMIT_EXCEED];
        yield put(signOut());
        yield put(confirmPaymentFailure({ modalMessage: errorConst.textLocale }));
        break;
      case ErrorType.SETTLE_FULLY_PURCHASE:
      case ErrorType.SETTLE_REMAIN_NOT_ENOUGH:
      case ErrorType.CBS_FULLY_PURCHASE:
      case ErrorType.CBS_REMAIN_NOT_ENOUGH:
        const payloadModal = {
          code: error.code,
          state: { message: error.modalMessage },
          actions: {
            onEdit: payloadOTP.backToEditUnit,
          },
        };
        yield put(openModal(payloadModal));
        yield put(confirmPaymentFailure({ ...error, ignore: true }));
        break;
      case ErrorType.REF_PRICING_NOT_MATCH:
        const payloadModalError = {
          code: error.code,
          state: { message: error.modalMessage },
          actions: {
            onEdit: payloadOTP.backToDetailProduct,
          },
        };
        yield put(openModal(payloadModalError));
        yield put(confirmPaymentFailure({ ...error, ignore: true }));
        break;
      case ErrorType.CHANNAL_STATUS_NOT_MATCH:
        const payloadModalErr = {
          code: error.code,
          state: { message: error.modalMessage },
          actions: {
            onEdit: payloadOTP.backToDetailProduct,
          },
        };
        yield put(openModal(payloadModalErr));
        yield put(confirmPaymentFailure({ ...error, ignore: true }));
        break;
      default:
        yield put(confirmPaymentFailure(error));
        break;
    }
  }
};

const getCustomerOutstandingSaga = function* () {
  try {
    const response = yield call(SecondMarketAPI.getCustomerOutstanding);
    let securitiesList = {
      securitiesList: response.data?.securitiesList?.map((sec) => {
        sec.bondTypeAlias = mappingBondTypeAliasToShort(sec.bondTypeAlias);
        sec.rating = sec.rating ? sec.rating : sec.bondTypeAlias === "GB" ? "GB" : "Non-Rated";
        return sec;
      }),
      totalWealth: response.data?.totalWealth,
    };
    yield put(getCustomerOutstandingSuccess({ securitiesList }));
  } catch (error) {
    yield put(getCustomerOutstandingFailure(error));
  }
};

const getSecuritiesDetailSaga = function* ({ payload: product }) {
  try {
    const response = yield call(SecondMarketAPI.getSecuritiesDetail, {
      securitiesCode: product.securitiesCode,
      brokerageAccNo: product.brokerageAccNo,
      offshoreFlag: product.offshoreFlag,
    });

    // cal Value for display
    let { securities: security, cashFlowList } = response.data ?? {};
    security = {
      ...security,
      cashFlowList,
      priceUnitCost: calculate(`${security.availableUnit || 0} * ${security.costPerUnit || 0}`),
      currentPrice: calculate(`${security.availableUnit || 0} * ${security.dirtyPriceValue || 0}`),
      brokerageAccNo: product.brokerageAccNo,
    };

    const productOffering = {
      ...security.productOfferingDetail,
      brokerageAccNo: product.brokerageAccNo,
    };

    yield call(initProductOfferingDetail, productOffering);
    yield put(
      getProductOfferingDetailSuccess({ productOffering, settlementDateList: productOffering.settlementDateList }),
    );

    yield put(getSecuritiesDetailSuccess({ security }));
    yield put(push(getRoutePath(Routes.Secondary.SecuritiesDetail, product.securitiesCode)));
  } catch (error) {
    yield put(getSecuritiesDetailFailure(error));
  }
};

const requestApproveLombardSaga = function* (action) {
  try {
    const secondMarketDetail = yield select(SecondMarketSelector.secondMarketDetail);
    const productOfferingDetail = yield select(SecondMarketSelector.productOfferingDetail);
    const locale = yield select(MasterSelector.locale);
    const acceptDiscList = secondMarketDetail.acceptDisc04
      ? concat(secondMarketDetail.acceptDiscList, DisclaimerType.AcceptWarningRisk)
      : secondMarketDetail.acceptDiscList;

    const payload = {
      acceptDiscList,
      securitiesCode: productOfferingDetail.securityCode,
      settlementDate: secondMarketDetail.settlementData?.settlementDate,
      loanAmt: action.payload.loanAmt,
      refPricingNo: secondMarketDetail.settlementData?.refPricingNo,
      unit: action.payload.unit,
      transType: secondMarketDetail.bankDirection,
      paymentAccountRef: action.payload.paymentAccountRef,
      language: locale,
    };

    yield call(SecondMarketAPI.requestApprove, payload);
    yield put(requestApproveLombardSuccess());

    if (action.payload?.onComplete) {
      action.payload?.onComplete();
    }
  } catch (error) {
    yield put(requestApproveLombardFailure(error));
  }
};

const cancelLombardSaga = function* ({ payload: drawdownId }) {
  try {
    yield call(SecondMarketAPI.cancelLombard, { drawdownId });
    yield put(cancelLombardSuccess());
    yield put(replace(Routes.LombardLoan + toUrlSearchParam({ tab: "histories" })));
  } catch (error) {
    yield put(cancelLombardFailure(error));
  }
};

const saga = [
  takeLatest(getProductOffering.type, getProductOfferingListSaga),
  takeLatest(getProductOfferingDetail.type, getProductOfferingDetailSaga),
  takeLeading(getConfirmationInfo.type, getConfirmationInfoSaga),
  takeLeading(sendOTP.type, sendOTPSaga),
  takeLeading(reSendOTP.type, reSendOTPSaga),
  takeLeading(confirmPayment.type, confirmPaymentSaga),
  takeLeading(requestApproveLombard.type, requestApproveLombardSaga),
  takeLeading(verifyReceiveAccount.type, verifyReceiveAccountSaga),
  takeLatest(getCustomerOutstanding.type, getCustomerOutstandingSaga),
  takeLatest(getSecuritiesDetail.type, getSecuritiesDetailSaga),
  takeLatest(cancelLombard.type, cancelLombardSaga),
];
export default saga;

const confirmPaymentFields = [
  "otpRef",
  "otpNo",
  "securityCode",
  "settlementDate",
  "direction",
  "accountPayment",
  "acceptDiscList",
  "unit",
  "paymentAccountRef",
  "receiveAccountRef",

  "receiveMethod",
  "securitiesCompCode",
  "securitiesCompAccNo",
  "brokerageAccNo",

  "dcmRiskTolerance",
  "dcmForeignCurrencyRisk",
  "dcmConfirm",
  "asset50MFlag",
  "income4MFlag",
  "investedAsset10MFlag",
  "sensitiveAge60UpExpBelow10Y",
  "sensitiveAge60UpExpOver10Y",
  "sensitiveNoneExp",
  "sensitivePrincipalLoss",
  "dcmRiskTolerance",
  "title",
  "gender",
  "email",
  "editOnProfile",
  "refPricingNo",
  "drawdownId",
  "repaymentFlag",

  "recommenderCode",
  "recommendBranchCode",
];
